import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  FileText, Download, Calendar, Building, Loader2, RefreshCw, Archive
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const StatementVault = ({ monoConnections = [] }) => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('1');

  const activeConnections = monoConnections.filter(c => 
    c.status === 'active' || c.status === 'connected'
  );

  useEffect(() => {
    fetchStatements();
  }, []);

  const fetchStatements = async () => {
    try {
      const res = await axios.get(`${API}/statement-vault`);
      setStatements(res.data.statements || []);
    } catch {
      // Silently fail - vault may be empty
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (statementId, filename) => {
    setDownloading(statementId);
    try {
      const res = await axios.get(`${API}/statement-vault/${statementId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'statement.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch {
      toast.error('Failed to download statement');
    } finally {
      setDownloading(null);
    }
  };

  const handleRequestStatement = async () => {
    if (!selectedConnection) {
      toast.error('Select a bank connection');
      return;
    }
    setRequesting(true);
    try {
      const period = parseInt(selectedPeriod);
      const statementType = period === 12 ? 'annual' : 'monthly';
      const res = await axios.post(`${API}/statement-vault/request`, {
        connection_id: selectedConnection,
        period,
        statement_type: statementType
      });
      if (res.data.success) {
        toast.success(res.data.message || 'Statement downloaded and saved');
        fetchStatements();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request statement');
    } finally {
      setRequesting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="statement-vault">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#108981]/10 rounded-lg">
              <Archive className="w-5 h-5 text-[#108981]" />
            </div>
            <div>
              <CardTitle className="text-lg">Statement Vault</CardTitle>
              <CardDescription>
                Auto-downloaded monthly and annual bank statements for audit
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchStatements} data-testid="refresh-vault">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Request Statement */}
        {activeConnections.length > 0 && (
          <div className="bg-slate-50 rounded-lg p-4 border space-y-3">
            <p className="text-sm font-medium">Request a Statement</p>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-muted-foreground mb-1 block">Bank Connection</label>
                <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                  <SelectTrigger className="h-9" data-testid="vault-select-connection">
                    <SelectValue placeholder="Select bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeConnections.map(conn => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.bank_name} ({conn.account_number?.slice(-4) || '****'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[140px]">
                <label className="text-xs text-muted-foreground mb-1 block">Period</label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="h-9" data-testid="vault-select-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last 1 Month</SelectItem>
                    <SelectItem value="3">Last 3 Months</SelectItem>
                    <SelectItem value="6">Last 6 Months</SelectItem>
                    <SelectItem value="12">Full Year (12 Months)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleRequestStatement}
                disabled={requesting || !selectedConnection}
                className="bg-[#108981] hover:bg-[#108981]/90 h-9"
                data-testid="vault-request-btn"
              >
                {requesting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Download className="w-4 h-4 mr-1" />
                )}
                {requesting ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          </div>
        )}

        {/* Statements List */}
        {statements.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">No statements yet</p>
            <p className="text-xs mt-1">
              {activeConnections.length > 0
                ? 'Statements are auto-downloaded monthly. You can also request one above.'
                : 'Connect a bank account to start auto-downloading statements.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {statements.map(stmt => (
              <div
                key={stmt.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                data-testid={`statement-${stmt.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stmt.statement_type === 'annual' ? 'bg-amber-100' : 'bg-blue-100'}`}>
                    <FileText className={`w-4 h-4 ${stmt.statement_type === 'annual' ? 'text-amber-600' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{stmt.bank_name}</p>
                      <Badge variant="outline" className="text-xs">
                        {stmt.statement_type === 'annual' ? 'Annual' : 'Monthly'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {stmt.period_label}
                      </span>
                      <span>{formatFileSize(stmt.file_size_bytes)}</span>
                      <span>Downloaded {formatDate(stmt.downloaded_at)}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(stmt.id, `${stmt.bank_name}_${stmt.period_label}.pdf`)}
                  disabled={downloading === stmt.id}
                  data-testid={`download-${stmt.id}`}
                >
                  {downloading === stmt.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Auto-download info */}
        <p className="text-xs text-muted-foreground border-t pt-3">
          Statements are auto-downloaded on the 2nd of each month (previous month) and January 2nd (full annual). 
          All PDFs are stored securely for audit and compliance.
        </p>
      </CardContent>
    </Card>
  );
};

export default StatementVault;
